import { metric_uom } from "common/constants/uom";
import { imperial_uom } from "common/constants/uom";
import { DropdownRecord, fromJSON } from "common/components/Dropdown/Dropdown";
import type { MeasurementTool } from "common/api/measurement.service";
import { MeasurementRecord } from "common/records/Measurement";
import { RootState } from "common/reducers";
import { currentJobId } from "common/selectors/job.selectors";
import { currentOrgSettingsAcl } from "common/selectors/org.selectors";
import { Nullable } from "common/types";
import { createSelector } from "reselect";
import { List } from "immutable";

// The subset of measurement tools that map to a real route occ-app can open.
const implementedMeasurementTools = [
  "annotator",
  "rapid_area",
  "satellite_measure",
  "fencing_tool",
] as const satisfies readonly MeasurementTool[];

const EMPTY_MEASUREMENT_TOOLS = List<MeasurementTool>();
const MEASUREMENTS_ACL_PATH = { path: "measurements", notSet: EMPTY_MEASUREMENT_TOOLS };

/**
 * Shown beneath a required Location field when the org has no Locations configured.
 */
export const NO_LOCATIONS_CONFIGURED_MESSAGE =
  "No locations configured for this org. Add them in Configuration → Measurements.";

const EMPTY_LOCATION_OPTIONS = List<DropdownRecord>();

/**
 * Looks to the currentJob in redux and gets the measurement_id from there
 *
 * @param {RootState} state RootState
 * @param {{}} options NONE
 * @returns {number} measurementId
 */
export const currentMeasurementId: (state: RootState) => Nullable<number> = createSelector(
  [currentJobId, (state: RootState, _) => state],
  (jobId: Nullable<number>, state) => {
    return state.getIn(["jobs", "byId", jobId, "measurement_id"]) as Nullable<number>;
  },
);

/**
 * Looks to the currentMeasurementId in redux and gets the measurement from there
 *
 * @param {RootState} state RootState
 * @param {{}} options NONE
 * @returns {MeasurementRecord | undefined} measurement, or undefined when none is loaded
 */
export const currentMeasurement = createSelector(
  [currentMeasurementId, (state: RootState) => state],
  (measurementId: Nullable<number>, state) => {
    return state.getIn(["measurements", "byId", measurementId]) as MeasurementRecord | undefined;
  },
);

/**
 * Looks to the loading value of current measurement in redux
 *
 * @param {RootState} state RootState
 * @param {{}} options NONE
 * @returns {Boolean}
 */
export const currentMeasurementLoaded = createSelector(
  [currentMeasurement, (state: RootState) => state],
  (measurement: Nullable<MeasurementRecord>) => {
    return measurement?.loading;
  },
);

/**
 * Looks to the currentMeasurementId in redux and gets the measurement uom
 *
 * @param state
 * @returns uom
 */
export const currentUoM = createSelector([currentMeasurement], (measurement) => {
  if (!measurement || measurement.system === "imperial") {
    return imperial_uom;
  } else {
    return metric_uom;
  }
});

/**
 * Looks to the currentMeasurementId in redux and gets the measurement locations for a dropdown
 *
 * @param state
 * @returns locations
 */
export const currentLocationOptions = createSelector([currentMeasurement], (measurement) => {
  return measurement?.locations
    ?.map((location) => fromJSON({ id: location, name: location }))
    .unshift(fromJSON({ id: "", name: "" }));
});

/**
 * Looks to the currentMeasurementId in redux and gets the measurement levels for a dropdown
 *
 * @param state
 * @returns levels
 */
export const currentLevelOptions = createSelector([currentMeasurement], (measurement) => {
  return measurement?.levels
    ?.map((level) => fromJSON({ id: level, name: level }))
    .unshift(fromJSON({ id: "", name: "" }));
});

/**
 * The org's configured location options without the leading blank entry, for the surfaces where a
 * Location is required — clearing is not a valid end state there, so no empty option is offered.
 * Every surface reads this same derivation, so the options and their order agree across the app.
 *
 * @param state
 * @returns {List<DropdownRecord>} location options, empty when the org has none configured
 */
export const currentRequiredLocationOptions = createSelector([currentMeasurement], (measurement) => {
  const locations = measurement?.locations;
  if (!locations || locations.isEmpty()) return EMPTY_LOCATION_OPTIONS;
  return locations.map((location) => fromJSON({ id: location, name: location }));
});

/**
 * Gets the measurement editing state
 *
 * @param state The RootState
 * @param options NONE
 * @returns {boolean} editing
 */
export const measurementEditing = (state: RootState, options?: any) => state.getIn(["measurements", "editing"]);

export const enabledMeasurementTools = createSelector(
  [(state: RootState) => currentOrgSettingsAcl(state, MEASUREMENTS_ACL_PATH)],
  (acl): List<MeasurementTool> => {
    const filtered = (acl ?? EMPTY_MEASUREMENT_TOOLS).filter((tool): tool is MeasurementTool =>
      implementedMeasurementTools.some((implemented) => implemented === tool),
    );
    return filtered.isEmpty() ? EMPTY_MEASUREMENT_TOOLS : filtered;
  },
);
